import { ADDRESS_ERROR_MESSAGES } from "@shared/contacts/address-copy";
import { addressInputSchema, initialContactAddress, withFirstAddressDefaults, type AddressInput } from "@shared/contacts/addresses";
import { API_PATHS } from "@shared/platform/app-metadata";
import type { Contact } from "@shared/platform/schema";

import { errorResponse, jsonResponse } from "./demo-responses";

type DemoAddress = AddressInput & { id: number; tenantId: number; contactId: number | null; archivedAt: string | null; createdBy: number; createdAt: string };
type Reserve = (collection: string) => Response | null;
type DemoAddressContext = { tenantId: number; userId: number; reserve: Reserve; now: () => string };

const CONTACT_ADDRESS_PATH = /^\/api\/contacts\/(\d+)\/addresses(?:\/(\d+))?(\/archive)?$/;

function clearReplacedDefaults(rows: DemoAddress[], contactId: number, input: AddressInput) {
  for (const row of rows.filter((address) => address.contactId === contactId)) {
    if (input.defaultBilling) row.defaultBilling = false;
    if (input.defaultShipping) row.defaultShipping = false;
  }
}

export function createDemoAddresses(context: DemoAddressContext) {
  const rows: DemoAddress[] = [];

  function insert(contactId: number, input: AddressInput): DemoAddress {
    const values = withFirstAddressDefaults(input, rows.filter((row) => row.contactId === contactId && row.archivedAt == null).length);
    clearReplacedDefaults(rows, contactId, values);
    const row = { ...values, id: rows.length + 1, tenantId: context.tenantId, contactId, archivedAt: null, createdBy: context.userId, createdAt: context.now() };
    rows.push(row);
    return row;
  }

  function activeRow(contactId: number, id: number) {
    return rows.find((row) => row.id === id && row.contactId === contactId && row.archivedAt == null);
  }

  function create(contactId: number, body: unknown): Response {
    const parsed = addressInputSchema.safeParse(body);
    if (!parsed.success) return errorResponse(parsed.error.message, 400);
    return context.reserve("addresses") ?? jsonResponse(insert(contactId, parsed.data), 201);
  }

  function update(contactId: number, id: number, body: unknown): Response {
    const parsed = addressInputSchema.safeParse(body);
    if (!parsed.success) return errorResponse(parsed.error.message, 400);
    const row = activeRow(contactId, id);
    if (!row) return errorResponse(ADDRESS_ERROR_MESSAGES.addressNotFound, 404);
    clearReplacedDefaults(rows, contactId, parsed.data);
    return jsonResponse(Object.assign(row, parsed.data));
  }

  function archive(contactId: number, id: number): Response {
    const row = activeRow(contactId, id);
    if (!row) return errorResponse(ADDRESS_ERROR_MESSAGES.addressNotFound, 404);
    return jsonResponse(Object.assign(row, { archivedAt: context.now(), defaultBilling: false, defaultShipping: false }));
  }

  function addInitial(contact: Pick<Contact, "id" | "address" | "country">) {
    const initial = initialContactAddress(contact);
    if (initial) insert(contact.id, initial);
  }

  function respond(method: string, path: string, body: unknown): Response | null {
    if (method === "GET" && path === API_PATHS.addresses) return jsonResponse(rows);
    const match = path.match(CONTACT_ADDRESS_PATH);
    if (!match) return null;
    const [, contactId, id, archiving] = match;
    if (method === "GET" && !id) return jsonResponse(rows.filter((row) => row.contactId === Number(contactId)));
    if (method === "POST" && !id) return create(Number(contactId), body);
    if (method === "PUT" && id && !archiving) return update(Number(contactId), Number(id), body);
    if (method === "POST" && id && archiving) return archive(Number(contactId), Number(id));
    return null;
  }

  return { respond, addInitial };
}
